import { useEffect, useState } from "react";
import { Button, Modal } from "semantic-ui-react";
import { FormError } from "../models/FormError";
import { UserFile } from "../models/UserFile";
import { FileMetaData } from "../models/FileMetaData";
import AddOrgUserFileForm, { FormData } from "./AddOrgUserFileForm";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (title: string, expiryDate: Date, fileMetaData: FileMetaData) => void;
  userFile?: UserFile;
  onUploadFile: (userFile: UserFile) => void;
  uploading: boolean;
}

const AddOrgUserFileModal = ({ open, onClose, onSubmit, userFile, onUploadFile, uploading }: Props) => {
  const [formData, setFormData] = useState<FormData>({} as FormData);
  const [formErrors, setFormErrors] = useState<FormError>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (!open) {
      setFormData({} as FormData);
      setFormErrors({} as FormError);
      setHasSubmitted(false);
    }
  }, [open]);

  useEffect(() => {
    if (!userFile?.uploadStatus) return;

    if (userFile.uploadStatus.uploaded) {
      setFormData({ ...formData, userFile });
    } else if (userFile.uploadStatus.uploadError) {
      setFormErrors({
        ...formErrors,
        userFile: userFile.uploadStatus?.uploadError.message,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFile]);

  const validateFormData = (formData: FormData) => {
    const formError: FormError = {};

    if (!formData.title) {
      formError.title = "Please enter title";
    }

    if (!formData.userFile || !formData.userFile.uploadStatus.uploaded || formData.userFile.uploadStatus.uploadError) {
      formError.userFile = "Please select a file";
    }

    return formError;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);

    const validationErrors = validateFormData(formData);
    setFormErrors(validationErrors);

    // If there is no validation error
    if (Object.keys(validationErrors).length === 0) {
      const { title, expiryDate, userFile } = formData;
      if (userFile) {
        const fileMetaData = {
          id: userFile.id,
          fileName: userFile.file.name,
          contentType: userFile.file.type,
        } as FileMetaData;
        onSubmit(title, expiryDate, fileMetaData);
      }
    }
  };

  const handleFormChange = (formData: FormData) => {
    if (hasSubmitted) {
      const validationErrors = validateFormData(formData);
      setFormErrors(validationErrors);
    }

    setFormData({ ...formData });
  };

  const handelClose = () => {
    onClose();
  };

  const handleAcceptedFilesSelected = (acceptedFiles: UserFile[]) => {
    if (acceptedFiles.length) {
      onUploadFile(acceptedFiles[0]);
      setFormErrors({});
    }
  };

  const handleRejectedFilesSelected = (rejectedFiles: UserFile[]) => {
    if (rejectedFiles.length) {
      setFormErrors({
        ...formErrors,
        userFile: rejectedFiles[0].uploadStatus?.fileErrors
          ? rejectedFiles[0].uploadStatus.fileErrors[0]
          : "Please select a file",
      });
    }
  };

  return (
    <Modal open={open}>
      <Modal.Header>Add Document</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <AddOrgUserFileForm
            formErrors={formErrors}
            formData={formData}
            onChange={handleFormChange}
            onAcceptedFilesSelected={handleAcceptedFilesSelected}
            onRejectedFilesSelected={handleRejectedFilesSelected}
          ></AddOrgUserFileForm>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handelClose}>Cancel</Button>
        <Button onClick={handleSubmit} positive disabled={uploading} loading={uploading}>
          Ok
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default AddOrgUserFileModal;
